import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Button from "@components/Button/Button";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import type { Company } from "@services/vessel-call/types";
import { t } from "i18next";
import { FormProvider, useFieldArray } from "react-hook-form";
import type { FieldArrayPath, Path } from "react-hook-form";
import { FORM_FIELDS } from "../../constants";
import { useCreateShippingAgencyTransfer } from "../../hooks/useCreateShippingAgencyTransfer";
import {
  getRequestShippingAgencyTransferFormSchema,
  type RequestShippingAgencyTransferFormData,
} from "../../schema/requestShippingAgencyTransferSchema";
import {
  PERSIST_KEY,
  useShippingAgencyTransferStore,
} from "../../store/useRequestShippingAgencyTransfer.store";
import { useUploadShippingAgencyTransferDocuments } from "./useUploadShippingAgencyTransferDocuments";

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const TEST_ID_BASE = "shipping-agency-transfer-request";
const ADDITIONAL_DOCUMENTS_MAX_FILES = 25;
const ADDITIONAL_DOCUMENTS_CERTIFICATE_TYPE = "ADDITIONAL_DOCUMENT";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

type AdditionalDocumentType =
  | "CERTIFICATE"
  | "PERMIT"
  | "DOCUMENT"
  | "INSURANCE"
  | "PRE_SAILING_MASTER_DECLARATION"
  | "VESSEL_MASTER_SOF"
  | "OTHER_SPECIFY";

type AdditionalDocument = {
  name: string;
  url: string;
  type?: string;
  documentType?: AdditionalDocumentType | "";
  otherSpecifyType?: string;
  documentName?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
  documentSummary?: string;
};

const ADDITIONAL_DOCUMENT_TYPES: AdditionalDocumentType[] = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "PRE_SAILING_MASTER_DECLARATION",
  "VESSEL_MASTER_SOF",
  "OTHER_SPECIFY",
];

const ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS: Record<AdditionalDocumentType, string> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  OTHER_SPECIFY: "other-specify",
};

const generateAdditionalDocumentField = {
  documentType: (idx: number) => `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.documentType` as const,
  otherSpecifyType: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.otherSpecifyType` as const,
  documentName: (idx: number) => `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.documentName` as const,
  issueDate: (idx: number) => `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.issueDate` as const,
  expiryDate: (idx: number) => `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.expiryDate` as const,
  documentSummary: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.documentSummary` as const,
};

const getDocumentEntriesFromValue = (value: unknown): AdditionalDocument[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is AdditionalDocument => {
    if (!entry || entry instanceof File || typeof entry !== "object") return false;
    const candidate = entry as Record<string, unknown>;
    return typeof candidate.name === "string" && typeof candidate.url === "string";
  });
};

const mergeAdditionalDocumentMetadata = (
  documents: AdditionalDocument[],
  metadataDocuments: AdditionalDocument[],
): AdditionalDocument[] => {
  const maxLength = Math.max(documents.length, metadataDocuments.length);
  return Array.from({ length: maxLength }, (_v, idx) => {
    const doc = documents[idx];
    const meta = metadataDocuments[idx];
    if (!doc) return meta;
    if (!meta) return doc;
    return {
      ...doc,
      ...meta,
      name: doc.name || meta.name,
      url: doc.url || meta.url,
      type: doc.type || meta.type,
    };
  }).filter((d): d is AdditionalDocument => Boolean(d));
};

const getDocumentKey = (document: UploadEntry, idx: number): string => {
  const entry = document as Record<string, unknown>;
  const url = typeof entry.url === "string" && entry.url.trim() ? entry.url : undefined;
  const name = typeof entry.name === "string" && entry.name.trim() ? entry.name : undefined;
  return url ?? name ?? `additional-document-${idx}`;
};

const hasUploadedDocumentUrl = (entry: UploadEntry): boolean => {
  if (!entry || entry instanceof File || typeof entry !== "object") return false;
  const candidate = entry as Record<string, unknown>;
  return typeof candidate.url === "string" && candidate.url.trim().length > 0;
};

const buildDateMinValue = (dateValue: unknown): Date | undefined => {
  if (!dateValue) return undefined;
  const date = new Date(dateValue as string | Date);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return date;
};

export type UpdateFormData = <K extends keyof RequestShippingAgencyTransferFormData>(
  key: K,
  value: RequestShippingAgencyTransferFormData[K] | undefined,
) => void;

interface ShippingAgencyTransferRequestProps {
  companies: Company[];
  currentShippingAgency: string;
  vesselCallId: string;
  vesselId?: string;
  vesselCallStatus?: string;
  departureStatus?: string;
}

const ShippingAgencyTransferRequest: React.FC<ShippingAgencyTransferRequestProps> = ({
  companies,
  currentShippingAgency,
  vesselId,
  vesselCallId,
  vesselCallStatus,
  departureStatus,
}) => {
  const setAll = useShippingAgencyTransferStore((s) => s.setAll);
  const resetForm = useShippingAgencyTransferStore((s) => s.resetForm);
  const setField = useShippingAgencyTransferStore((s) => s.setField);
  const { notify } = useNotification();

  const filesByKey = useUploadStore((state) => state.filesByKey);
  const uploadsInFlightByKey = useUploadStore((state) => state.uploadsInFlightByKey);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const formDefaults = useMemo(
    () => useShippingAgencyTransferStore.getState() as RequestShippingAgencyTransferFormData,
    [],
  );

  const schema = useMemo(() => getRequestShippingAgencyTransferFormSchema(t), []);

  const form = usePersistentForm<RequestShippingAgencyTransferFormData>({
    schema,
    defaultValues: formDefaults,
    storeValues: formDefaults,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: PERSIST_KEY,
    dateKeys: [FORM_FIELDS.TRANSFER_DATE_AND_TIME],
  });

  const additionalDocumentsFieldName =
    FORM_FIELDS.ADDITIONAL_DOCUMENTS as FieldArrayPath<RequestShippingAgencyTransferFormData>;

  const { remove: removeAdditionalDocumentAt } = useFieldArray({
    control: form.control,
    name: additionalDocumentsFieldName,
  });

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const uploadConditions = useMemo(
    () => ({ ...FILE_UPLOAD_PROFILES.additionalDocuments }),
    [FILE_UPLOAD_PROFILES.additionalDocuments],
  );

  const additionalDocumentTypeOptions = ADDITIONAL_DOCUMENT_TYPES.map((value) => ({
    label: t(
      `${LANG_BASE}.additional-documents.document-types.${ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS[value]}`,
    ),
    value,
  }));

  const additionalDocumentFiles =
    filesByKey[FORM_FIELDS.ADDITIONAL_DOCUMENTS] ?? EMPTY_UPLOAD_ENTRIES;
  const isAdditionalDocumentsUploading =
    (uploadsInFlightByKey[FORM_FIELDS.ADDITIONAL_DOCUMENTS] ?? 0) > 0;

  const rawAdditionalDocuments = form.watch(
    FORM_FIELDS.ADDITIONAL_DOCUMENTS as unknown as Path<RequestShippingAgencyTransferFormData>,
  ) as AdditionalDocument[] | undefined;

  const watchedAdditionalDocuments =
    rawAdditionalDocuments ?? (EMPTY_UPLOAD_ENTRIES as unknown as AdditionalDocument[]);

  const hasInitializedAdditionalDocuments = useRef(false);

  // Sync watched additional documents back to Zustand store
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setField(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, rawAdditionalDocuments ?? ([] as any));
  }, [rawAdditionalDocuments, setField]);

  // Start each request with empty additional documents.
  useEffect(() => {
    if (hasInitializedAdditionalDocuments.current) return;
    hasInitializedAdditionalDocuments.current = true;

    form.setValue(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, [] as any, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    setField(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, [] as any);
    useUploadStore.getState().setFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS, [], {
      mode: "replace",
      maxFiles: ADDITIONAL_DOCUMENTS_MAX_FILES,
    });
    form.clearErrors(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any);
  }, [form, setField]);

  const updateFormData = useCallback<UpdateFormData>(
    (key, value) => {
      setField(key as any, value as any);
    },
    [setField],
  );

  const setAdditionalDocumentValue = useCallback(
    (documents: AdditionalDocument[]) => {
      form.setValue(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, documents as any, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      setField(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, documents as any);
    },
    [form, setField],
  );

  const handleAdditionalDocumentUploadSuccess = useCallback(
    ({
      documentUrls,
      startIdx,
      files,
      storeKey,
    }: {
      documentUrls: string[];
      startIdx: number;
      files: File[];
      storeKey?: string;
    }) => {
      if (storeKey !== FORM_FIELDS.ADDITIONAL_DOCUMENTS) return;

      const nextDocuments = [
        ...getDocumentEntriesFromValue(form.getValues(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any)),
      ];

      documentUrls.forEach((url, fileOffset) => {
        const at = startIdx + fileOffset;
        const uploadedFile = files[fileOffset];
        const fileName = uploadedFile?.name ?? `document-${at + 1}`;
        const existingDocument = nextDocuments[at];
        nextDocuments[at] = {
          ...existingDocument,
          name: fileName,
          url,
          type: uploadedFile?.type,
          documentType: existingDocument?.documentType ?? "",
          otherSpecifyType: existingDocument?.otherSpecifyType ?? "",
          documentName: existingDocument?.documentName ?? "",
          issueDate: existingDocument?.issueDate ?? null,
          expiryDate: existingDocument?.expiryDate ?? null,
          documentSummary: existingDocument?.documentSummary ?? "",
        };
      });

      const uploadedDocuments = nextDocuments.filter((doc) => !!doc?.url);
      const normalizedDocuments = uploadedDocuments.slice(-ADDITIONAL_DOCUMENTS_MAX_FILES);

      setAdditionalDocumentValue(normalizedDocuments);
      setFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS, normalizedDocuments, {
        mode: "replace",
        maxFiles: ADDITIONAL_DOCUMENTS_MAX_FILES,
      });
      form.clearErrors(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any);
    },
    [form, setAdditionalDocumentValue, setFilesForKey],
  );

  const handleAdditionalDocumentUploadError = useCallback(
    (error: Error, meta: { startIdx: number; count: number; storeKey?: string }) => {
      notify({
        type: "error",
        title: t(`${LANG_BASE}.notifications.request-submitted-error-title`),
        description: t(`${LANG_BASE}.notifications.request-submitted-error-description`),
      });
      console.error("Error uploading additional document:", error);

      const toRemoveDescending = Array.from(
        { length: meta.count },
        (_v, idx) => meta.startIdx + idx,
      ).reverse();

      toRemoveDescending.forEach((idx) => {
        useUploadStore.getState().removeFileAtIndex(meta.storeKey!, idx);
      });
    },
    [notify],
  );

  const uploadMutation = useUploadShippingAgencyTransferDocuments({
    handleUploadSuccess: handleAdditionalDocumentUploadSuccess,
    handleUploadError: handleAdditionalDocumentUploadError,
  });

  const handleDocumentUpload = useCallback(
    (_key: string, files: File[]) => {
      if (!files?.length) return;
      const storedDocuments = getDocumentEntriesFromValue(
        useUploadStore.getState().getFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS),
      );
      const formDocuments = getDocumentEntriesFromValue(
        form.getValues(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any),
      );
      const currentDocuments = mergeAdditionalDocumentMetadata(storedDocuments, formDocuments);

      setAdditionalDocumentValue(currentDocuments);
      uploadMutation.mutate({
        files,
        startIdx: currentDocuments.length,
        certificateType: ADDITIONAL_DOCUMENTS_CERTIFICATE_TYPE,
        storeKey: FORM_FIELDS.ADDITIONAL_DOCUMENTS,
      });
    },
    [form, setAdditionalDocumentValue, uploadMutation],
  );

  const handleDocumentReject = useCallback(() => {
    const typesLabel = uploadConditions.format.join(", ");
    form.setError(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any, {
      type: "manual",
      message: t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    });
  }, [uploadConditions, form]);

  const handleRemoveDocument = useCallback(
    (index: number) => {
      const currentUploadEntries = useUploadStore
        .getState()
        .getFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS) as UploadEntry[];

      if (index < 0 || index >= currentUploadEntries.length) return;

      if (!hasUploadedDocumentUrl(currentUploadEntries[index])) return;

      const uploadedIndex =
        currentUploadEntries.slice(0, index + 1).filter(hasUploadedDocumentUrl).length - 1;

      if (uploadedIndex < 0) return;

      // Let RHF own array row removal to keep remaining row values stable.
      removeAdditionalDocumentAt(uploadedIndex);
    },
    [removeAdditionalDocumentAt],
  );

  const handleSetDocumentFiles = useCallback(
    (nextFiles: UploadEntry[]) => {
      const storedDocuments = getDocumentEntriesFromValue(
        useUploadStore.getState().getFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS),
      );
      const formDocuments = getDocumentEntriesFromValue(
        form.getValues(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any),
      );
      const currentDocuments = mergeAdditionalDocumentMetadata(storedDocuments, formDocuments);
      const incomingUploadedDocuments = getDocumentEntriesFromValue(nextFiles);
      const incomingFiles = nextFiles.filter((file): file is File => file instanceof File);
      const mergedDocuments = mergeAdditionalDocumentMetadata(
        incomingUploadedDocuments,
        currentDocuments,
      );

      setFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS, [...mergedDocuments, ...incomingFiles], {
        mode: "replace",
        maxFiles: ADDITIONAL_DOCUMENTS_MAX_FILES,
      });
    },
    [form, setFilesForKey],
  );

  const validateUploadFiles = useCallback((incomingFiles: File[]) => {
    const existingCount =
      useUploadStore.getState().getFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS).length ?? 0;
    if (existingCount + incomingFiles.length > ADDITIONAL_DOCUMENTS_MAX_FILES) {
      return t("form-validation.max-images", { max: ADDITIONAL_DOCUMENTS_MAX_FILES });
    }
    return null;
  }, []);

  const clearForm = useCallback(() => {
    clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);
    resetForm();
    form.reset();
  }, [clearFilesForKey, resetForm, form]);

  const { onSubmit: createTransfer } = useCreateShippingAgencyTransfer({
    vesselCallId,
    vesselId,
    resetForm: clearForm,
    vesselCallStatus,
    departureStatus,
  });

  const companiesOptions = useMemo(() => {
    const opts = companies
      .filter((c) => c.companyCode !== currentShippingAgency)
      .map((c) => ({ label: c.companyNameEnglish, value: c.companyCode }));

    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [companies, currentShippingAgency]);

  const onSubmit = useMemo(() => form.handleSubmit(() => createTransfer()), [form, createTransfer]);

  const renderAdditionalDocumentMetadata = (idx: number) => {
    const document = watchedAdditionalDocuments[idx];
    const documentType = document?.documentType;
    const issueDate = document?.issueDate;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FormItem<RequestShippingAgencyTransferFormData>
          testId={`additional-documents-${idx}-document-type`}
          name={generateAdditionalDocumentField.documentType(idx) as any}
          type="select"
          options={additionalDocumentTypeOptions}
          label={t(`${LANG_BASE}.additional-documents.document-type`)}
        />
        {documentType === "OTHER_SPECIFY" && (
          <FormItem<RequestShippingAgencyTransferFormData>
            testId={`additional-documents-${idx}-other-specify`}
            name={generateAdditionalDocumentField.otherSpecifyType(idx) as any}
            type="text"
            maxChars={30}
            label={t(`${LANG_BASE}.additional-documents.other-specify`)}
          />
        )}
        {documentType !== "OTHER_SPECIFY" && <div className="hidden lg:block" aria-hidden="true" />}
        <div className="lg:col-span-2">
          <FormItem<RequestShippingAgencyTransferFormData>
            testId={`additional-documents-${idx}-document-name`}
            name={generateAdditionalDocumentField.documentName(idx) as any}
            type="text"
            maxChars={50}
            label={t(`${LANG_BASE}.additional-documents.document-name`)}
          />
        </div>
        <FormItem<RequestShippingAgencyTransferFormData>
          testId={`additional-documents-${idx}-issue-date`}
          name={generateAdditionalDocumentField.issueDate(idx) as any}
          type="date-picker"
          label={t(`${LANG_BASE}.additional-documents.issue-date`)}
          onFieldChange={() => {
            form.trigger([
              generateAdditionalDocumentField.issueDate(idx) as any,
              generateAdditionalDocumentField.expiryDate(idx) as any,
            ]);
          }}
          timePicker={false}
          dateConstraint="not-future"
        />
        <FormItem<RequestShippingAgencyTransferFormData>
          testId={`additional-documents-${idx}-expiry-date`}
          name={generateAdditionalDocumentField.expiryDate(idx) as any}
          type="date-picker"
          label={t(`${LANG_BASE}.additional-documents.expiry-date`)}
          onFieldChange={() => {
            form.trigger([
              generateAdditionalDocumentField.issueDate(idx) as any,
              generateAdditionalDocumentField.expiryDate(idx) as any,
            ]);
          }}
          timePicker={false}
          dateConstraint="not-past"
          minDate={buildDateMinValue(issueDate)}
        />
        <div className="lg:col-span-2">
          <FormItem<RequestShippingAgencyTransferFormData>
            testId={`additional-documents-${idx}-document-summary`}
            name={generateAdditionalDocumentField.documentSummary(idx) as any}
            type="text"
            maxChars={300}
            label={t(`${LANG_BASE}.additional-documents.document-summary`)}
          />
        </div>
      </div>
    );
  };

  const hasReachedMaxFiles = additionalDocumentFiles.length >= ADDITIONAL_DOCUMENTS_MAX_FILES;
  let uploadedDocumentIndex = -1;

  return (
    <FormProvider {...form}>
      <div>
        <div className="flex gap-x-6 gap-y-2">
          <FormItem<RequestShippingAgencyTransferFormData, typeof FORM_FIELDS.NEW_SHIPPING_AGENCY>
            name={FORM_FIELDS.NEW_SHIPPING_AGENCY}
            type="autocomplete"
            options={companiesOptions}
            label={t(`${LANG_BASE}.form-fields.new-shipping-agency`)}
            testId={`${TEST_ID_BASE}-new-shipping-agency`}
            onFieldChange={updateFormData}
          />
          <FormItem<
            RequestShippingAgencyTransferFormData,
            typeof FORM_FIELDS.TRANSFER_DATE_AND_TIME
          >
            name={FORM_FIELDS.TRANSFER_DATE_AND_TIME}
            type="date-picker"
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.TRANSFER_DATE_AND_TIME}`)}
            testId="transfer-date-and-time"
            onFieldChange={updateFormData}
            dateConstraint="not-past"
            combinedInput
          />
        </div>

        <div className="mt-6">
          <FormSection title={t(`${LANG_BASE}.additional-documents.title`)}>
            <div className="col-span-full flex flex-col">
              <FormItem<RequestShippingAgencyTransferFormData>
                name={FORM_FIELDS.ADDITIONAL_DOCUMENTS as any}
                type="file"
                uploadConditions={uploadConditions}
                testId="additional-documents-file"
                files={additionalDocumentFiles}
                setFiles={handleSetDocumentFiles}
                onUpload={handleDocumentUpload}
                onReject={handleDocumentReject}
                disabled={isAdditionalDocumentsUploading || hasReachedMaxFiles}
                maxFiles={ADDITIONAL_DOCUMENTS_MAX_FILES}
                notSuccess={isAdditionalDocumentsUploading}
                validateDrop={(selectedCount) => {
                  if (selectedCount > ADDITIONAL_DOCUMENTS_MAX_FILES) {
                    return t("form-validation.max-images", {
                      max: ADDITIONAL_DOCUMENTS_MAX_FILES,
                    });
                  }
                  return null;
                }}
                fileValidation={validateUploadFiles}
              />
              {additionalDocumentFiles.map((document, idx) => {
                const documentKey = getDocumentKey(document, idx);
                const hasUploadedUrl = Boolean((document as Record<string, unknown>).url);
                const metadataIndex = hasUploadedUrl ? ++uploadedDocumentIndex : -1;

                return (
                  <div className="grid grid-cols-1 gap-4" key={documentKey}>
                    <SingleFile
                      testId={`document-additional-${idx}`}
                      file={document}
                      fieldName={FORM_FIELDS.ADDITIONAL_DOCUMENTS}
                      currentFileIndex={idx}
                      setFiles={() => handleRemoveDocument(idx)}
                      removeFn={() => form.clearErrors(FORM_FIELDS.ADDITIONAL_DOCUMENTS as any)}
                      isLoading={isAdditionalDocumentsUploading && document instanceof File}
                    />
                    {hasUploadedUrl && renderAdditionalDocumentMetadata(metadataIndex)}
                  </div>
                );
              })}
            </div>
          </FormSection>
        </div>
        <div className="pt-8 pb-4 px-0">
          <Button
            type="button"
            variant="contained"
            color="primary"
            label={t("common.submit")}
            testId={`${TEST_ID_BASE}-submit-button`}
            sx={{ height: "40px", minHeight: "40px", textTransform: "none" }}
            onClick={onSubmit}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default ShippingAgencyTransferRequest;
